import { ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { NewsFrequencyOptions, useNewsFrequencyData } from '@/hooks';
import { TimeWindow } from '@/types';

import { ChartHeader, EmptyState, LoadingSpinner } from './ChartScaffold';

export interface NewsFrequencyChartPoint {
  date: string;
  count: number;
}

export interface NewsFrequencyChartProps {
  symbol: string;
  timeWindow: TimeWindow;
  onTimeWindowChange: (window: TimeWindow) => void;
  title: string;
  description: string;
  timeWindowLabels: Record<TimeWindow, string>;
  emptyStateMessage: string;
  emptyStateHint?: string | null;
  tooltipLabelFormatter?: (value: string) => string;
  tooltipValueFormatter?: (value: number) => string;
  xAxisTickFormatter?: (
    value: string,
    effectiveTimeWindow: TimeWindow
  ) => string;
  seriesLabel?: string;
  barColor?: string;
  footerNote?: ReactNode;
  footerClassName?: string;
  cardClassName?: string;
  chartContainerHeight?: string;
  queryOptions?: NewsFrequencyOptions;
  showTimeWindowControls?: boolean;
}

export const NewsFrequencyChart = ({
  symbol,
  timeWindow,
  onTimeWindowChange,
  title,
  description,
  timeWindowLabels,
  emptyStateMessage,
  emptyStateHint,
  tooltipLabelFormatter,
  tooltipValueFormatter,
  xAxisTickFormatter,
  seriesLabel = 'Articole',
  barColor = '#6b7280',
  footerNote,
  footerClassName = 'mt-2 text-xs text-neutral-500',
  cardClassName = 'mt-8 shadow-md',
  chartContainerHeight = 'h-64',
  queryOptions,
  showTimeWindowControls = true,
}: NewsFrequencyChartProps) => {
  const { data: newsFrequencyData, isLoading: newsFrequencyLoading } =
    useNewsFrequencyData(symbol, timeWindow, queryOptions);

  const frequencyData =
    (newsFrequencyData?.frequency as NewsFrequencyChartPoint[]) ?? [];

  const effectiveTimeWindow =
    (newsFrequencyData?.timeWindow as TimeWindow | undefined) ?? timeWindow;

  return (
    <Card className={cardClassName}>
      <CardHeader>
        <ChartHeader
          title={title}
          description={description}
          timeWindow={timeWindow}
          onTimeWindowChange={onTimeWindowChange}
          labels={timeWindowLabels}
          showDateRange={false}
          showTimeWindowSelector={showTimeWindowControls}
        />
      </CardHeader>
      <CardContent>
        <div className={`${chartContainerHeight} w-full`}>
          {newsFrequencyLoading ? (
            <LoadingSpinner />
          ) : frequencyData.length === 0 ? (
            <EmptyState message={emptyStateMessage} hint={emptyStateHint} />
          ) : (
            <div className="relative h-full w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={frequencyData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value: string) => {
                      if (xAxisTickFormatter) {
                        return xAxisTickFormatter(value, effectiveTimeWindow);
                      }

                      if (
                        effectiveTimeWindow === 'max' ||
                        effectiveTimeWindow === '1year'
                      ) {
                        return value;
                      }

                      return value.toString();
                    }}
                    tick={{ fontSize: 10 }}
                    height={30}
                    angle={-45}
                    textAnchor="end"
                    interval={
                      effectiveTimeWindow === 'max' ? 'preserveStartEnd' : 0
                    }
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip
                    labelFormatter={(value) =>
                      tooltipLabelFormatter
                        ? tooltipLabelFormatter(value as string)
                        : `Dată: ${value}`
                    }
                    formatter={(value: number) => [
                      tooltipValueFormatter
                        ? tooltipValueFormatter(value)
                        : `${value}`,
                      seriesLabel,
                    ]}
                  />
                  <Legend />
                  <Bar
                    dataKey="count"
                    fill={barColor}
                    radius={[4, 4, 0, 0]}
                    name={seriesLabel}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        {footerNote && <div className={footerClassName}>{footerNote}</div>}
      </CardContent>
    </Card>
  );
};
